import { useEffect, useState } from 'react';

/**
 * Layout and input-capability queries for the overlay.
 *
 * Two separate questions, deliberately not conflated:
 *
 *  - *How much room is there* (`useIsCompact`) decides layout — stacking the
 *    corner readouts, turning a modal into a full-screen sheet. A narrow
 *    desktop window deserves the same treatment as a phone.
 *  - *Can the player touch the screen* (`useIsTouch`) decides whether the
 *    on-screen sticks exist at all. A 27" touchscreen is not compact and a
 *    hidden phone browser window is not a mouse.
 *
 * Both are feature queries, never `navigator.userAgent` — same rule
 * `Capabilities.ts` follows for the GPU.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = (): void => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * Viewport too small to show every readout group at once.
 *
 * Narrow *or* short, and the second half matters as much as the first: a
 * landscape phone is 844px wide and 390px tall, so a width-only test calls it
 * a desktop and hands it the full five-group HUD, which then overlaps itself
 * top to bottom. Matches the compact-chrome breakpoint at the end of
 * styles.css. Duplicated here rather than read from CSS because the *initial*
 * HUD set has to be chosen before anything is laid out, and a stylesheet
 * cannot answer a question React asks in a `useState` initialiser.
 */
export const COMPACT_QUERY = '(max-width: 560px), (max-height: 560px)';

/**
 * True for a *phone* held upright — not any portrait viewport.
 *
 * The game is a landscape experience: a cockpit is wider than it is tall and
 * the instrument groups run along the bottom edge, so a phone in portrait is
 * asked to turn rather than given a squeezed second layout to maintain. The
 * width clause is what keeps that from insulting a tablet, which has room for
 * the full corner HUD in either orientation and should never be told to
 * rotate. Read from the viewport rather than `screen.orientation`, which
 * reports the *device's* orientation and so still says "landscape" for a
 * landscape-locked tablet whose browser window is a tall split-screen pane.
 */
export const PORTRAIT_PHONE_QUERY = '(orientation: portrait) and (max-width: 620px)';

export function useIsPortraitPhone(): boolean {
  return useMediaQuery(PORTRAIT_PHONE_QUERY);
}

/**
 * True when the primary pointer is a finger.
 *
 * `?touch=1` forces it on so the on-screen controls can be exercised from a
 * desktop browser (and from a capture script) without a phone in hand;
 * `?touch=0` forces it off for a device that reports coarse pointers but is
 * being driven by a mouse.
 */
export function useIsTouch(): boolean {
  const coarse = useMediaQuery('(pointer: coarse)');
  const override =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('touch') : null;
  if (override === '1') return true;
  if (override === '0') return false;
  return coarse;
}
